<template>
  <div class="row px-3">
    <div class="col-12 mb-4 noprint">
			<span class="back-btn" @click="$emit('setCurrentTab','PaymentDetailsForm')">
				<font-awesome-icon icon="arrow-left" size="sm" />
				Back
			</span>
    </div>

    <div class="col-12">
      <p>
        Please preview the details you've provided and scroll down to submit.
      </p>
      <div class="title-text font-weight-bold">
        Invoice details
      </div>
      <div class="row my-2">
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.COMPANY_AND_CODE_NAME}}</div>
          <div>{{filteredFormData.companyCode}} - {{filteredFormData.companyCodeText}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.SUPPLIER_ID}}</div>
          <div>{{filteredFormData.supplierNumber}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.NAME_OF_SUPPLIER}}</div>
          <div>{{filteredFormData.supplierName}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.INVOICE_AMOUNT}} <span class="font-italic">{{FORM_LABELS.INCL_TAX_VAT_IF_ANY}}</span></div>
          <div>{{filteredFormData.currency}} {{filteredFormData.invoicedAmount}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.invoiceDate">
          <div class="form-text">{{FORM_LABELS.INVOICE_DATE}}</div>
          <div>{{filteredFormData.invoiceDate}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.invoiceNumber">
          <div class="form-text">{{FORM_LABELS.INVOICE_REFERENCE_NUMBER}}</div>
          <div>{{filteredFormData.invoiceNumber}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.PAYMENT_DUE_DATE}}</div>
          <div>{{filteredFormData.paymentDueDate}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.deliveryLocation">
          <div class="form-text">{{FORM_LABELS.SHIP_TO_LOCATION}}</div>
          <div>{{deliveryLocationText}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.invoiceReferenceForCreditMemo">
          <div class="form-text">{{FORM_LABELS.INVOICE_REFERENCE_FOR_CREDIT_MEMO}}</div>
          <div>{{filteredFormData.invoiceReferenceForCreditMemo}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.invoiceDescription">
          <div class="form-text">{{FORM_LABELS.DESCRIPTION_OF_INVOICE}}</div>
          <div class="wrap-text">{{filteredFormData.invoiceDescription}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">{{FORM_LABELS.EXPENSE_TYPE}}</div>
          <div>{{filteredFormData.expenseTypeCode}} <strong>/</strong> {{filteredFormData.expenseType}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.specialInstructions">
          <div class="form-text">{{FORM_LABELS.SPECIAL_INSTRUCTIONS}}</div>
          <div class="wrap-text">{{filteredFormData.specialInstructions}}</div>
        </div>
        <div class="col-12 px-0 mt-3" v-if="filteredFormData.invoiceItems && filteredFormData.invoiceItems.length > 0">
          <div class=" ml-3 form-text mb-1">{{FORM_LABELS.INVOICE_DETAILS}}</div>
          <invoice-details
              :invoices="filteredFormData.invoiceItems"
              :currency="filteredFormData.currency"
              :net-amount="filteredFormData.netAmount"
              :company-code="filteredFormData.companyCode"/>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.taxAmount">
          <div class="form-text">{{FORM_LABELS.TAX_VAT_AMOUNT}}</div>
          <div>{{filteredFormData.currency}} {{filteredFormData.taxAmount}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="mt-2"><span class="font-weight-bold mr-1">{{FORM_LABELS.TOTAL_AMOUNT}}</span>
            <header-with-tooltip :tooltip-text="FORM_LABELS.TOTAL_AMOUNT_SUBDESC"></header-with-tooltip>
          </div>
          <div>{{filteredFormData.currency}} {{filteredFormData.totalAmount}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="getFloatValue(filteredFormData.unmatchedAmount) > 0">
          <div class="mt-2"><span class="font-weight-bold mr-1">{{FORM_LABELS.UNMATCHED_AMOUNT}}</span>
            <header-with-tooltip :tooltip-text="unmatchedAmountToolTip"></header-with-tooltip>
          </div>
          <div>{{filteredFormData.currency}} {{filteredFormData.unmatchedAmount}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.assignment">
          <div class="mt-2"><span class="font-weight-bold mr-1">{{FORM_LABELS.ASSIGNMENT}}</span>
            <div>{{filteredFormData.assignment}}</div>
          </div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.docHeaderText">
          <div class="mt-2"><span class="font-weight-bold mr-1">{{FORM_LABELS.DOC_HEADER_TEXT}}</span>
            <div>{{filteredFormData.docHeaderText}}</div>
          </div>
        </div>
      </div>

      <preview-form-payment-details :filteredFormData="filteredFormData" />

      <preview-attachments :filteredFormData="filteredFormData" :viewAttachment="true"/>

      <div class="pt-3">
        Please submit a copy of the request for <a href="javascript:window.print();" class="solution-link">invoice without a purchase order form</a> and original invoices to the respective Autodesk local office
        for the following countries for filing as required by the local Tax authorities: Argentina, Brazil, China,
        Colombia, Czech Republic, India, Israel, Italy, South Korea (non-digital tax Invoice or not 전자세금계산서), Mexico,
        Poland, Qatar, Russia, Saudi Arabia, Taiwan, Turkey and UAE
      </div>


      <div class="col-12 px-xl-0 pt-5 noprint">
        <div class="d-flex flex-column align-items-center justify-content-xl-end flex-xl-row">
          <button class="btn-container flat-btn gray-btn close-btn btn mx-2 my-2" @click="navigateToHome" :disabled="isLoading">
            Cancel
          </button>
          <button class="btn btn-outline-info outline-flat-btn submit-btn" @click="saveDraftNonPOBasedPaymentRequest(true)">Save Draft</button>
          <button class="btn primary-blue-btn flat-btn mt-2 mt-xl-0 ml-xl-2"
                  @click="submitNonPOPaymentData(filteredFormData)" :disabled="isLoading">
            Submit
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep"
import InvoiceDetails from "@/components/Payments/NonPOBasedPayment/InvoiceDetails";
import PreviewFormPaymentDetails from "@/components/Payments/PaymentDetailsForm/PreviewFormPaymentDetails";
import HeaderWithTooltip from "@/components/HeaderWithTooltip";
import PaymentFormsMixin from "@/mixins/PaymentFormsMixin";
import api from "@/api"
import CONSTANTS from "@/store/constants";
import PreviewAttachments from "@/components/Payments/PreviewAttachments";

export default {
  name: "PreviewNonPOBasedPaymentFormDetails",
  props: {
    formData: String
  },
  mixins: [PaymentFormsMixin],
  components: {InvoiceDetails,PreviewFormPaymentDetails, HeaderWithTooltip, PreviewAttachments},
  data() {
    return {
      filteredFormData: {},
      deliveryLocationText: ''
    }
  },
  computed: {
    unmatchedAmountToolTip() {
      return this.FORM_LABELS.UNMATCHED_AMOUNT_SUBDESC
    }
  },
  mounted() {
    this.filterDataForPreviewAndSubmit();
  },
  methods: {
    filterDataForPreviewAndSubmit() {
      this.filteredFormData = cloneDeep(this.$store.getters[this.formData])

      this.deliveryLocationText = this.getTextValue(this.adminDataList.companyBusinessPlaceList, this.filteredFormData.deliveryLocation)
      this.filteredFormData.expenseTypeCode = this.getTextValue(this.adminDataList.expenseTypeCodeOptions, this.filteredFormData.expenseTypeCode)
      this.filteredFormData.expenseType = this.getTextValue(this.adminDataList.expenseTypeTextOptions, this.filteredFormData.expenseType)
      this.filteredFormData.netAmount = this.filteredFormData.netAmount.toString()
      this.filteredFormData.totalAmount = this.filteredFormData.totalAmount.toString()
      this.filteredFormData.unmatchedAmount = this.filteredFormData.unmatchedAmount ? this.filteredFormData.unmatchedAmount.toString() : 0
      this.filteredFormData.currency = this.getTextValue(this.adminDataList.currencyList, this.filteredFormData.currency)
      this.filteredFormData.paymentType = this.getTextValue(this.adminDataList.paymentTypeOptions, this.filteredFormData.paymentType)


      //set requesterEmail to current user email
      this.filteredFormData.requesterEmail = this.currentUser.email

      //Deleting formType and attachmentUrl properties when trying to submit after editing a draft
      delete this.filteredFormData.formType;
      delete this.filteredFormData.attachmentUrl;

    },
    async submitNonPOPaymentData(formData) {
      formData = this.removeAttachmentFileSizes(formData)
      this.setIsLoading(true)
      this.setIsFormDataLoaded(false)
      this.setFormValidationErrors([])

      try {
        const resp = await api.submitNonPOBasedFormData(formData)
        let returnMessages = resp.data.data.submitNonPurchaseOrder.webformResponse.returnMessages
        if(returnMessages && returnMessages.length > 0) {
          let statusId = returnMessages[0].messageId
          let webformSubmissionStatus = await this.getSupplierSubmissionStatusById(statusId)
          if (webformSubmissionStatus && webformSubmissionStatus.status === CONSTANTS.WEBFORM_SUBMISSION_STATUS.SUBMITTED) {
            let userMessages = this.buildMessageString(webformSubmissionStatus)
            this.launchSuccessModal(userMessages, 'Supplier Central - Non PO Payment form submit success')
            this.setIsInvoicesAndDraftsCached(true)
          } else if (webformSubmissionStatus && webformSubmissionStatus.status === CONSTANTS.WEBFORM_SUBMISSION_STATUS.FAILED) {
            this.setFormValidationErrors(webformSubmissionStatus.responseMessages)
            this.setIsFormDataLoaded(true)
            this.validateAndSetCurrentTab('NonPOPaymentQuestionnaireOne')
            //Reset invoices dashboard to re-fetch tracker data
            this.setFormattedInvoices([])
          } else {
            this.launchFailedModal(CONSTANTS.ERROR_MESSAGE, 'Supplier Central - Non PO Payment form api error')
          }
        }
      } catch {
        this.launchFailedModal(CONSTANTS.ERROR_MESSAGE, 'Supplier Central - Non PO Payment form api error')
      } finally {
        this.setIsLoading(false)
      }
    },
  }
}
</script>

<style>
.form-text {
  font-weight: bold;
  margin-top: 10px;
}

.title-text {
  padding: 0;
  margin: 8px 0;
}

</style>